import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, TableContainer, useTheme } from '@mui/material';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { formBundleSignatureRequestApi, formsSignatureRequestApi } from 'app/services/api';
import DocumentView from 'app/shared/components/document/DocumentView';
import { ModalSize } from 'app/shared/componentsRestyled/coreDesignSystem/Modals';
import dayjs from 'dayjs';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { COMMON_DATETIME_FORMAT_FOR_FILE_NAME } from 'constants/date';
import { FeatureFlag } from 'constants/featureFlags';
import { PAGE_CODES } from 'constants/pageCodes';
import useLoanId from 'hooks/useLoanId';
import useSort from 'hooks/useSort';
import { printFileFromBlob } from 'utils/file';
import { logger } from 'utils/logger';
import { downloadOrShareBlob } from 'utils/nativeFile';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { openDialogAction, showMessageAction } from 'store/global/actions';
import { selectFeatureFlagByKey } from 'store/global/selectors';

import { UnderwritingTestIds } from '../../../../Underwriting.testIds';

import { getLoanBundleHistoryTableConfig } from './LoanBundleHistoryTable.config';
import { getStyles } from './LoanBundleHistoryTable.styles';

const defaultOrder = {
  direction: 'desc',
  id: 'lastModified',
};

const LoanBundleHistoryTable = ({
  signatureItems,
  formBundleSignatureRequestId,
  isLoading,
  setIsLoading,
  createdDate,
  formBundleName,
  isSignable,
}) => {
  const isDownloadable = !!formBundleName && !!formBundleSignatureRequestId;

  const dispatch = useDispatch();
  const { order, onRequestSort } = useSort(defaultOrder);

  const loanId = useLoanId();

  const theme = useTheme();

  const classes = getStyles(theme);

  const { isManageAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.FORM_BUNDLE_SIGNATURE_REQUEST),
  );

  const isFormsConsolidationEnabled = useSelector(
    selectFeatureFlagByKey(FeatureFlag.FormsConsolidationEnabled),
  );

  const signatureRequestApi = isFormsConsolidationEnabled
    ? formsSignatureRequestApi
    : formBundleSignatureRequestApi;

  const orderedSignatureItems = useMemo(
    () =>
      _.orderBy(
        signatureItems,
        [item => item[order.id]?.toString()?.toLowerCase()],
        [order.direction],
      ).map(item => ({ ...item, id: item.formBundleSignatureRequestItemId })),
    [signatureItems, order],
  );

  const tableConfig = useMemo(() => getLoanBundleHistoryTableConfig({ isSignable }), [isSignable]);

  const getDocumentCall = useCallback(
    () =>
      signatureRequestApi.getPdfDocumentBundle({
        loanId,
        signatureId: formBundleSignatureRequestId,
      }),
    [signatureRequestApi, loanId, formBundleSignatureRequestId],
  );

  const onDownloadBundle = useCallback(() => {
    if (isDownloadable) {
      setIsLoading(true);

      signatureRequestApi
        .getPdfDocumentBundle({
          loanId,
          signatureId: formBundleSignatureRequestId,
        })
        .then(({ data }) => {
          const formattedDate = dayjs(createdDate).format(COMMON_DATETIME_FORMAT_FOR_FILE_NAME);
          const fileName = `${formBundleName} ${formattedDate}`;

          return downloadOrShareBlob(data, fileName);
        })
        .catch(error => {
          dispatch(showMessageAction({ message: "Couldn't download bundle", variant: 'error' }));
          logger.error(error);
        })
        .finally(() => setIsLoading(false));
    }
  }, [
    isDownloadable,
    signatureRequestApi,
    setIsLoading,
    loanId,
    formBundleSignatureRequestId,
    createdDate,
    formBundleName,
    dispatch,
  ]);

  const onPrintDocument = useCallback(() => {
    getDocumentCall()
      .then(res => printFileFromBlob(res.data))
      .catch(err => {
        dispatch(showMessageAction({ message: 'Could not print document', variant: 'error' }));
        logger.error(err);
      });
  }, [dispatch, getDocumentCall]);

  const onViewDocuments = useCallback(() => {
    dispatch(
      openDialogAction({
        header: 'View Documents',
        size: ModalSize.XL,
        hasScrollY: false,
        body: () => (
          <Box sx={classes.root}>
            <DocumentView
              isPrintable
              isView
              docId="signature"
              getDocumentCall={getDocumentCall}
              isControlVisible={false}
              isDownloadable={isDownloadable}
              onDownloadDocument={onDownloadBundle}
              onPrintDocument={onPrintDocument}
            />
          </Box>
        ),
      }),
    );
  }, [classes.root, dispatch, getDocumentCall, isDownloadable, onDownloadBundle, onPrintDocument]);

  return (
    <Box className="w-full flex flex-col">
      {isManageAllowed && (
        <Box className="flex items-center justify-end p-16" sx={classes.header}>
          <Button
            data-testid={UnderwritingTestIds.getLoanBundleHistoryViewDocumentsButtonId(
              formBundleSignatureRequestId,
            )}
            disabled={isLoading}
            onClick={onViewDocuments}
          >
            View Documents
          </Button>
        </Box>
      )}

      <TableContainer>
        <Box
          data-testid={UnderwritingTestIds.getFormBundleTableId(formBundleSignatureRequestId)}
          sx={classes.table}
        >
          <TableCDS
            isStickyHeader
            config={tableConfig}
            data={orderedSignatureItems}
            order={order}
            onRequestSort={onRequestSort}
          />
        </Box>
      </TableContainer>
    </Box>
  );
};

LoanBundleHistoryTable.propTypes = {
  signatureItems: PropTypes.array.isRequired,
  formBundleSignatureRequestId: PropTypes.string.isRequired,
  isSignable: PropTypes.bool,
  ...WithLoadingPropTypes,
};

export default withLoading(LoanBundleHistoryTable);
